import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export function useDialogFocus(ref: React.RefObject<HTMLElement | null>, open: boolean, onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open || !ref.current) return;
    const panel = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    const focusable = () => Array.from(panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
    )).filter(el => el.getClientRects().length > 0);
    (focusable()[0] || panel).focus();
    const onKey = (e: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('[aria-modal="true"]');
      if (dialogs.length && !panel.contains(dialogs[dialogs.length - 1]) && dialogs[dialogs.length - 1] !== panel) return;
      if (e.key === "Escape") { e.preventDefault(); closeRef.current(); }
      if (e.key === "Tab") {
        const items = focusable();
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) { e.preventDefault(); panel.focus(); }
        else if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); if (previous?.isConnected) previous.focus(); };
  }, [open, ref]);
}

const EXIT_MS = 140;

/** Keeps an overlay mounted for its short exit animation after `open` turns false. */
export function useExitTransition(open: boolean) {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) { setMounted(true); return; }
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => setMounted(false), reduce ? 0 : EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);
  return open || mounted;
}

export default function Dialog({ open, onClose, title, children, wide = false }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const id = React.useId();
  useDialogFocus(ref, open, onClose);
  const mounted = useExitTransition(open);
  // Callers often clear the state that fed the dialog when they close it, so
  // the exit frame keeps showing the last open content instead of a blank panel.
  const lastContent = useRef({ title, children });
  if (open) lastContent.current = { title, children };
  if (!mounted) return null;
  const closing = !open;
  return createPortal(
    <div className={`dialog-backdrop ${closing ? "is-closing" : ""}`} onClick={closing ? undefined : onClose} aria-hidden={closing || undefined}>
      <div ref={ref} role="dialog" aria-modal={closing ? undefined : "true"} aria-labelledby={id} tabIndex={-1}
        className={`dialog-panel ${wide ? "dialog-wide" : ""}`} onClick={e => e.stopPropagation()}>
        <div className="dialog-heading"><h2 id={id}>{lastContent.current.title}</h2><button type="button" className="icon-button" aria-label="Close dialog" onClick={onClose} disabled={closing}><X size={20} /></button></div>
        <div className="dialog-body">{lastContent.current.children}</div>
      </div>
    </div>, document.body,
  );
}
